/** API client. */
const BASE = "/api";

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(body?.detail || body?.title || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

let accessToken: string | null = null;
let workspaceId: string | null = null;
let authPaused = false; // quando true, rejeita chamadas até relogin (preserva IndexedDB)

export function isAuthPaused() {
  return authPaused;
}

export function resumeAuth() {
  authPaused = false;
}

function pauseAuth() {
  authPaused = true;
  // Limpa apenas o token de acesso (não toca em IndexedDB / outbox).
  accessToken = null;
  localStorage.removeItem("takite.token");
  try {
    window.dispatchEvent(new CustomEvent("takite:auth-required"));
  } catch {}
}

export function setAccessToken(t: string | null) {
  accessToken = t;
  if (t) {
    localStorage.setItem("takite.token", t);
    authPaused = false;
  } else {
    localStorage.removeItem("takite.token");
  }
}

export function setWorkspaceId(id: string | null) {
  workspaceId = id;
  if (id) localStorage.setItem("takite.ws", id);
  else localStorage.removeItem("takite.ws");
}

export function getWorkspaceId(): string | null {
  return workspaceId || localStorage.getItem("takite.ws");
}

export function loadFromStorage() {
  accessToken = localStorage.getItem("takite.token");
  workspaceId = localStorage.getItem("takite.ws");
}

async function refreshToken(): Promise<boolean> {
  try {
    const r = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!r.ok) {
      // 401/403 → refresh inválido. NÃO apaga IndexedDB; só pausa auth.
      if (r.status === 401 || r.status === 403) pauseAuth();
      return false;
    }
    const data = await r.json();
    setAccessToken(data.access_token);
    return true;
  } catch {
    // Falha de rede: NÃO pausa auth (provavelmente offline). Apenas falha o refresh.
    return false;
  }
}

export async function api<T = any>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  if (authPaused) {
    throw new ApiError(401, { detail: "auth_paused", title: "auth_paused" });
  }
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (workspaceId) headers.set("X-Workspace-Id", workspaceId);
  try {
    const m = sessionStorage.getItem("takite.privacy.mode");
    const exp = Number(sessionStorage.getItem("takite.privacy.expires") || 0);
    if (m === "private" && exp > Date.now()) {
      headers.set("X-Privacy-Mode", "private");
    }
  } catch {}
  if (!(init.body instanceof FormData) && init.body) {
    headers.set("Content-Type", "application/json");
  }

  let res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });

  if (res.status === 401 && accessToken) {
    if (await refreshToken()) {
      headers.set("Authorization", `Bearer ${accessToken}`);
      res = await fetch(`${BASE}${path}`, {
        ...init,
        headers,
        credentials: "include",
      });
    } else if (authPaused) {
      throw new ApiError(401, { detail: "auth_paused", title: "auth_paused" });
    }
  }

  if (!res.ok) {
    let body: any = null;
    try {
      const text = await res.text();
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
